import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { ASSETS } from "@/lib/projects";
import Lightbox from "@/components/Lightbox";

const About = () => {
  const { t } = useLang();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  return (
    <div className="bg-white pt-32 md:pt-40 pb-24 md:pb-32" data-testid="about-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Eyebrow header */}
        <div className="flex items-center gap-4 mb-8">
          <span className="w-10 h-[1px] bg-[#E11D2E]" />
          <span className="eyebrow">{t.about.eyebrow}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Portrait */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="relative overflow-hidden bg-black/5 aspect-[4/5] w-full block cursor-zoom-in group"
              aria-label="Vergroot portret"
              data-testid="about-portrait-button"
            >
              <img
                src={ASSETS.portrait}
                alt="Jesse Feenstra portrait"
                className="absolute inset-0 w-full h-full object-cover img-zoom"
                data-testid="about-portrait"
              />
              <div className="absolute bottom-6 left-6 bg-white px-4 py-2">
                <p className="font-display text-sm font-medium">
                  Jesse Feenstra
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-black/60 mt-0.5">
                  Founder · Visual Designer
                </p>
              </div>
            </button>
          </div>

          {/* Text */}
          <div className="lg:col-span-7">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight">
              {t.about.title}
            </h1>
            <p className="mt-6 text-xl md:text-2xl font-light text-black/70 leading-snug max-w-xl">
              {t.about.subtitle}
            </p>

            <div className="mt-12 space-y-6 text-base md:text-lg text-black/75 leading-relaxed max-w-2xl">
              {t.about.bodyParagraphs.map((p, i) => (
                <p key={i} data-testid={`about-paragraph-${i + 1}`}>
                  {p}
                </p>
              ))}
            </div>

            {/* Skills */}
            <div className="mt-16 pt-12 border-t border-black/10">
              <div className="flex items-center gap-4 mb-8">
                <span className="w-8 h-[1px] bg-[#E11D2E]" />
                <span className="eyebrow">{t.about.skillsTitle}</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8" data-testid="skills-list">
                {t.about.skills.map((skill, i) => (
                  <li
                    key={skill}
                    className="flex items-center gap-4 py-4 border-b border-black/10 group"
                    data-testid={`skill-item-${i + 1}`}
                  >
                    <span className="font-display text-xs text-black/40 tabular-nums">
                      0{i + 1}
                    </span>
                    <span className="font-display text-lg font-medium tracking-tight group-hover:text-[#E11D2E] transition-colors">
                      {skill}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-16">
              <Link to="/contact" className="fd-btn fd-btn-outline-dark" data-testid="about-cta-contact">
                {t.nav.contact} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Lightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        item={{
          id: "portrait",
          src: ASSETS.portrait,
          alt: "Jesse Feenstra",
          title: "Jesse Feenstra",
          category: "Founder · Visual Designer",
        }}
      />
    </div>
  );
};

export default About;
